import { useServices } from '@/shared/hooks/use-services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { queryClient } from '@/shared/lib/query-client';
import { usePriceCalculation } from '@/modules/stores/hooks/use-price-calculation';
import { useAddonManagement } from '@/modules/stores/hooks/use-addon-management';
import { useFilteredAddons } from '@/modules/stores/hooks/use-filtered-addons';

import type { ProductSchema } from '../../pages/product/schema';

import { UIError } from '@/shared/utils/ui-error';
import { readClubRedeemContext } from '@/modules/club/hooks/use-club-redeem';
import { formatPoints, pointsForPrice } from '@/modules/club/utils/points';
import type { AddonCategory, SelectedAddon } from '../../types/addons';
import type { ComboGroup, GetStoreItemsRequest } from '../../types/get-store-items';
import { useQuantityPromotionNote } from '../../hooks/use-quantity-promotion-note';
import {
  buildComboSteps,
  calculateComboChoicesPrice,
  findComboSelection,
  fromCartComboChoices,
  getComboGroupStepKey,
  getComboStepBlocker,
  getFirstBlockedComboStep,
  getMaxChoices,
  getNextComboStepIndex,
  getSortedComboGroups,
  isChoiceCombo,
  setComboChoiceAddons,
  setComboOptionQuantity,
  toCartComboChoices,
  toggleComboOption,
  validateComboSelections,
  type ComboSelection,
} from '../../utils/combo';
import {
  applyAddonChange,
  checkMinimumRequirements,
  checkMaximumRequirements,
  getCategoryLimitMessage,
  getAddonLimitLabel,
} from '../../utils/addon';
import { getEffectivePrice } from '../../utils/promotion';

import { useGetMerchantDetails } from '../../hooks/http/use-get-merchant-details';

import { useTracking } from '@/modules/analytics/hooks/use-tracking';
import { enhancedToast } from '@/shared/utils/enhanced-toast';
import {
  familyForItem,
  packQuantity,
  quantityStep,
  tierPriceFor,
} from '@/shared/utils/wholesale-pricing';

export function useProductDrawerModel(
  productId: string | null,
  slug: string,
  onCloseDrawer: () => void,
  // PRATDEV-245: a Nova Venda monta o produto com o cardápio do balcão. No do
  // delivery, item sem "Delivery (App)" não é achado e o modal fica carregando.
  canal?: GetStoreItemsRequest['canal']
) {
  const [, navigate] = useLocation();
  const { getStoreItems, addItem, clearCart, getItem, editItem, listItems } = useServices();

  const track = useTracking();

  // Captura editItemId da URL e armazena em estado local
  // Isso garante que não perdemos a referência quando a URL é limpa
  const [editItemIdState] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('editItemId');
  });

  const urlParams = new URLSearchParams(window.location.search);
  const fromCart = urlParams.get('fromCart');
  const editItemId = editItemIdState; // Usa o estado ao invés da URL
  const isEditMode = !!editItemId;

  // Clube pratiGO (PRAT-27): `?clube=1` abre o produto em modo resgate; o
  // contexto (multiplicador e saldo) foi deixado pela tela do Clube.
  const [clubRedeemContext] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('clube') !== '1') return null;
    const context = readClubRedeemContext();
    return context && context.slug === slug ? context : null;
  });

  console.log('🎯 ProductDrawerModel inicializado:', {
    productId,
    slug,
    editItemId,
    fromCart,
    urlParams: window.location.search,
    timestamp: Date.now(),
  });

  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [addonSearchTerm, setAddonSearchTerm] = useState('');
  const [animatingBadge, setAnimatingBadge] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  // Escolhas dos grupos do combo configurável (PRAT-20). Vazio em produto comum.
  const [comboSelections, setComboSelections] = useState<ComboSelection[]>([]);
  // Etapa atual da montagem do combo em etapas. `reviewing`: o cliente veio do
  // resumo corrigir uma escolha e deve voltar a ele ao terminar aquele grupo.
  const [comboWizard, setComboWizard] = useState({ index: 0, reviewing: false });
  const comboAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref para rastrear o último productId processado
  const lastProductIdRef = useRef<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const form = useForm<ProductSchema>({
    defaultValues: {
      item_id: productId!,
      addons: [],
      total_price: 0,
      quantity: 1,
      observations: '',
    },
  });

  const { watch, setValue, getValues } = form;

  // Quantidade mínima permitida para um item no carrinho
  const MIN_QUANTITY = 1;

  // Busca detalhes da loja para verificar se está aberta
  const merchantDetailsQuery = useGetMerchantDetails({
    slug: slug!,
  });

  // Busca o produto usando React Query
  const { data: storeItems } = useQuery({
    queryFn: () => getStoreItems.execute({ slug: slug!, canal }),
    queryKey: canal ? ['store-items', slug, canal] : ['store-items', slug],
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const cartItemQuery = useQuery({
    queryKey: ['cart-item', editItemId],
    queryFn: () => {
      console.log('📦 Buscando item do carrinho:', editItemId);
      return getItem.execute({ id: editItemId! });
    },
    enabled: !!editItemId,
    staleTime: 0, // Sempre buscar dados frescos
    gcTime: 1 * 60 * 1000, // 1 minuto
    refetchOnMount: true,
  });

  // Log quando os dados do item chegam
  useEffect(() => {
    if (cartItemQuery.data) {
      console.log('✅ Dados do item carregados com sucesso:', {
        itemId: cartItemQuery.data.id,
        itemName: cartItemQuery.data.name,
        quantity: cartItemQuery.data.quantity,
        editItemId,
        match: cartItemQuery.data.id === editItemId,
      });
    }
    if (cartItemQuery.error) {
      console.error('❌ Erro ao carregar item:', cartItemQuery.error);
    }
  }, [cartItemQuery.data, cartItemQuery.error, editItemId]);

  console.log('📦 CartItemQuery status:', {
    isLoading: cartItemQuery.isLoading,
    isError: cartItemQuery.isError,
    hasData: !!cartItemQuery.data,
    data: cartItemQuery.data,
  });

  const product = useMemo(() => {
    if (!storeItems?.items || !productId) {
      console.log('❌ Produto não encontrado:', { hasItems: !!storeItems?.items, productId });
      return null;
    }
    const foundProduct = Object.values(storeItems.items)
      .flat()
      .find(item => item.id === productId);

    console.log('🔍 Produto encontrado:', foundProduct ? foundProduct.name : 'não encontrado');
    return foundProduct;
  }, [storeItems?.items, productId]);

  // Promoção por quantidade (PRAT-184): "Promoção: 2º item com 50% — vale para
  // Hambúrguers" quando o produto participa.
  const quantityPromotionNote = useQuantityPromotionNote(storeItems, product);

  // Uma visualização por produto aberto. `track` é um objeto novo a cada
  // render: com ele nas dependências o evento saía várias vezes (PRAT-19).
  useEffect(() => {
    if (product) {
      track.productView({
        id: product.id,
        name: product.name,
        price: parseFloat(product.price.replace('R$ ', '').replace(',', '.')),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Filtro de addons usando hook customizado
  const filteredAddonCategories = useFilteredAddons(product, addonSearchTerm);

  // Valores do formulário
  const quantity = watch('quantity') || 1;
  const watchedAddons = watch('addons');
  const addons = useMemo(() => (watchedAddons || []) as SelectedAddon[], [watchedAddons]);

  // Preço base efetivo do produto: usa o preço de venda promocional quando há
  // promoção válida; caso contrário, o preço cheio. É este valor que é usado no
  // cálculo do total e levado ao carrinho/checkout.
  const effectivePrice = getEffectivePrice(product);

  // Combo configurável (PRAT-20): grupos de escolha do produto (vazio em produto
  // comum e em combo fixo) e as escolhas no formato do carrinho, cujo acréscimo
  // (preço adicional + complementos das escolhas) entra no preço por unidade.
  const comboGroups = useMemo(
    () => getSortedComboGroups(product?.combo_groups),
    [product?.combo_groups]
  );
  const isCombo = isChoiceCombo(product) && comboGroups.length > 0;
  const comboChoices = useMemo(
    () => toCartComboChoices(comboGroups, comboSelections),
    [comboGroups, comboSelections]
  );
  const comboValidation = useMemo(
    () => validateComboSelections(comboGroups, comboSelections),
    [comboGroups, comboSelections]
  );

  // Montagem em etapas: grupo → complementos do escolhido → … → resumo.
  const productAddonCategories = useMemo(
    () => product?.addon_categories ?? [],
    [product?.addon_categories]
  );
  const comboSteps = useMemo(
    () => (isCombo ? buildComboSteps(comboGroups, comboSelections, productAddonCategories) : []),
    [isCombo, comboGroups, comboSelections, productAddonCategories]
  );
  const comboStepIndex = Math.max(0, Math.min(comboWizard.index, comboSteps.length - 1));
  const comboStep = comboSteps[comboStepIndex] ?? null;
  const comboStepBlocker = comboStep
    ? getComboStepBlocker(comboStep, comboSelections, addons, productAddonCategories)
    : '';
  const comboFirstBlocked = isCombo
    ? getFirstBlockedComboStep(comboSteps, comboSelections, addons, productAddonCategories)
    : null;

  // O avanço automático roda depois de um setTimeout; lê as etapas atuais.
  const comboStepsRef = useRef(comboSteps);
  useEffect(() => {
    comboStepsRef.current = comboSteps;
  }, [comboSteps]);

  const clearComboAdvance = () => {
    if (comboAdvanceTimerRef.current) clearTimeout(comboAdvanceTimerRef.current);
    comboAdvanceTimerRef.current = null;
  };

  useEffect(
    () => () => {
      if (comboAdvanceTimerRef.current) clearTimeout(comboAdvanceTimerRef.current);
    },
    []
  );

  // Cada etapa começa do topo.
  useEffect(() => {
    if (isCombo) scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [isCombo, comboStepIndex]);

  const handleComboNext = () => {
    clearComboAdvance();
    if (comboStepBlocker) return;
    setComboWizard(prev => {
      const index = getNextComboStepIndex(comboSteps, comboStepIndex, prev.reviewing);
      return { index, reviewing: prev.reviewing && index < comboSteps.length - 1 };
    });
  };

  const handleComboBack = () => {
    clearComboAdvance();
    setComboWizard(prev => ({ ...prev, index: Math.max(0, comboStepIndex - 1) }));
  };

  // Toque numa escolha do resumo: volta àquela etapa e, ao terminar, ao resumo.
  const handleComboGoToStep = (key: string) => {
    clearComboAdvance();
    const index = comboSteps.findIndex(step => step.key === key);
    if (index >= 0) setComboWizard({ index, reviewing: true });
  };

  // Escolha única: avança sozinho após uma pausa curta para o feedback visual,
  // desde que o cliente ainda esteja na mesma etapa.
  const scheduleComboAdvance = () => {
    if (!isCombo || !comboStep) return;
    clearComboAdvance();
    const stepKey = comboStep.key;
    comboAdvanceTimerRef.current = setTimeout(() => {
      comboAdvanceTimerRef.current = null;
      setComboWizard(prev => {
        const steps = comboStepsRef.current;
        const index = Math.max(0, Math.min(prev.index, steps.length - 1));
        if (steps[index]?.key !== stepKey) return prev;
        const next = getNextComboStepIndex(steps, index, prev.reviewing);
        return { index: next, reviewing: prev.reviewing && next < steps.length - 1 };
      });
    }, 350);
  };

  // Cálculo de preço total usando hook customizado
  const totalPrice = usePriceCalculation(
    effectivePrice + calculateComboChoicesPrice(comboChoices),
    quantity,
    addons,
    product?.addon_categories || []
  );

  // PRAT-187: atacado por faixa. A faixa sai da soma da família (sabores) com o
  // que já está na sacola; o botão mostra o preço com atacado (a API recalcula).
  const wholesaleFamily = useMemo(
    () => familyForItem(storeItems?.wholesale_pricing ?? null, product?.id ?? productId),
    [storeItems?.wholesale_pricing, product?.id, productId]
  );
  const wholesaleCartQuery = useQuery({
    queryKey: ['cart-items'],
    queryFn: async () => await listItems.execute(),
    enabled: !!wholesaleFamily,
    staleTime: 0,
  });
  const wholesaleUnitsInCart = useMemo(() => {
    if (!wholesaleFamily) return 0;
    const ids = new Set((wholesaleFamily.item_ids ?? []).map(String));
    return (wholesaleCartQuery.data ?? [])
      .filter(item => ids.has(String(item.item_id)) && item.id !== editItemId)
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }, [wholesaleFamily, wholesaleCartQuery.data, editItemId]);
  const wholesaleStep = quantityStep(wholesaleFamily);
  const wholesaleTotalPrice = useMemo(() => {
    if (!wholesaleFamily || !quantity) return null;
    const unit = totalPrice / quantity;
    const tier = tierPriceFor(wholesaleFamily, wholesaleUnitsInCart + quantity);
    return tier !== null && tier < unit
      ? Math.round((totalPrice - (unit - tier) * quantity) * 100) / 100
      : null;
  }, [wholesaleFamily, totalPrice, quantity, wholesaleUnitsInCart]);
  // Toda quantidade passa pela caixa: a mínima que fecha a caixa e, daí, de caixa em caixa.
  const handleSetQuantity = (value: number) =>
    setValue(
      'quantity',
      packQuantity(wholesaleFamily, wholesaleUnitsInCart, Math.max(MIN_QUANTITY, value))
    );

  // Família "só caixa fechada": produto novo já abre com o que fecha a caixa.
  const wholesaleDefaultRef = useRef<string | null>(null);
  useEffect(() => {
    if (!wholesaleFamily || editItemId || !productId || wholesaleStep < 2) return;
    if (!wholesaleCartQuery.isFetched || wholesaleDefaultRef.current === productId) return;
    const missing = wholesaleUnitsInCart % wholesaleStep;
    // Depois do reset de "produto novo" (efeito mais abaixo), que volta para 1.
    const timer = setTimeout(() => {
      wholesaleDefaultRef.current = productId;
      setValue('quantity', missing > 0 ? wholesaleStep - missing : wholesaleStep);
    }, 0);
    return () => clearTimeout(timer);
  }, [
    wholesaleFamily,
    editItemId,
    productId,
    wholesaleStep,
    wholesaleCartQuery.isFetched,
    wholesaleUnitsInCart,
    setValue,
  ]);

  // Atualiza o preço total no form sempre que o totalPrice mudar
  useEffect(() => {
    setValue('total_price', totalPrice);
  }, [totalPrice, setValue]);

  // Controla a animação do badge
  useEffect(() => {
    if (animatingBadge) {
      const timer = setTimeout(() => {
        setAnimatingBadge(null);
      }, 600); // Duração da animação
      return () => clearTimeout(timer);
    }
  }, [animatingBadge]);

  // Monitora mudanças nos addons para scroll automático
  useEffect(() => {
    if (addons.length === 0 || !product?.addon_categories) return;

    // Encontra a última categoria que foi modificada
    const lastAddon = addons[addons.length - 1];
    if (!lastAddon) return;

    const addonData = product?.addon_categories
      ?.flatMap(cat => cat.addons)
      .find(a => a.id === lastAddon.id);

    const lastModifiedCategory = addonData?.addon_category_id;
    if (!lastModifiedCategory) return;

    // Verifica se a categoria está completada
    const category = product?.addon_categories?.find(cat => cat.id === lastModifiedCategory);
    if (!category) return;

    const totalSelected = addons
      .filter(addon => {
        const addonData = product?.addon_categories
          ?.flatMap(cat => cat.addons)
          .find(a => a.id === addon.id);
        return addonData?.addon_category_id === lastModifiedCategory;
      })
      .reduce((total, addon) => total + addon.quantity, 0);

    // Categoria está completa quando atingiu o select_max (se definido)
    // Para categorias sem select_max (0), não há limite
    const hasMaxLimit = category.select_max > 0;
    const isMaxReached = hasMaxLimit && totalSelected >= category.select_max;

    // Categoria está completa se atingiu o máximo OU (select_max === 1 e tem pelo menos 1 item)
    const isCompleted = isMaxReached || (category.select_max === 1 && totalSelected > 0);

    if (isCompleted) {
      console.log('✅ Categoria completada:', category.name, {
        totalSelected,
        select_max: category.select_max,
        mandatory: category.mandatory,
      });

      // Busca a próxima categoria (prioriza obrigatórias não preenchidas, depois qualquer próxima)
      const allCategories = product?.addon_categories || [];
      const currentIndex = allCategories.findIndex(cat => cat.id === lastModifiedCategory);

      let nextCategoryToScroll: typeof category | null = null;

      // Primeiro, busca a próxima categoria obrigatória não preenchida
      for (let i = currentIndex + 1; i < allCategories.length; i++) {
        const nextCategory = allCategories[i];

        if (nextCategory.mandatory !== 1) continue;

        const nextTotalSelected = addons
          .filter(addon => {
            const addonData = product?.addon_categories
              ?.flatMap(cat => cat.addons)
              .find(a => a.id === addon.id);
            return addonData?.addon_category_id === nextCategory.id;
          })
          .reduce((total, addon) => total + addon.quantity, 0);

        // Calcula o mínimo necessário para a próxima categoria
        const nextMinRequired = nextCategory.select_min > 0 ? nextCategory.select_min : 1;

        // Se a próxima categoria obrigatória não atende ao mínimo, faz scroll para ela
        if (nextTotalSelected < nextMinRequired) {
          nextCategoryToScroll = nextCategory;
          console.log('➡️ Próxima categoria obrigatória não preenchida:', nextCategory.name);
          break;
        }
      }

      // Se não encontrou categoria obrigatória, busca a próxima categoria qualquer
      if (!nextCategoryToScroll) {
        const nextCategory = allCategories[currentIndex + 1];
        if (nextCategory) {
          nextCategoryToScroll = nextCategory;
          console.log('➡️ Próxima categoria:', nextCategory.name);
        }
      }

      // Faz scroll para a próxima categoria ou para observações
      if (nextCategoryToScroll) {
        setTimeout(() => {
          const categoryElement = document.querySelector(
            `[data-category-id="${nextCategoryToScroll!.id}"]`
          );
          if (categoryElement && scrollContainerRef.current) {
            const scrollContainer = scrollContainerRef.current;
            const elementRect = categoryElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const offset = 80;

            scrollContainer.scrollTo({
              top: scrollContainer.scrollTop + elementRect.top - containerRect.top - offset,
              behavior: 'smooth',
            });
          }
        }, 200);
      } else {
        // Se não há mais categorias, vai para observações
        console.log('✅ Todas categorias preenchidas, indo para observações');
        setTimeout(() => {
          const observationsElement = document.querySelector('[data-observations-section]');
          if (observationsElement && scrollContainerRef.current) {
            const scrollContainer = scrollContainerRef.current;
            const elementRect = observationsElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const offset = 80;

            scrollContainer.scrollTo({
              top: scrollContainer.scrollTop + elementRect.top - containerRect.top - offset,
              behavior: 'smooth',
            });
          }
        }, 200);
      }
    }
  }, [addons, product?.addon_categories]);

  // Gerenciamento de addons usando hook customizado.
  // getAddonQuantity/isAddonSelected seguem sendo usados pelos handlers internos;
  // a seleção em si é exposta de forma reativa via `selectedAddons` (ver return),
  // para que o conteúdo do drawer derive o estado diretamente da fonte reativa.
  const { updateAddonsList, getAddonQuantity, isAddonSelected } = useAddonManagement(
    product?.addon_categories || [],
    addons,
    (field, value) => setValue(field, value)
  );

  const handleAddonChange = (
    addonId: string,
    quantity: number,
    categoryId: string,
    selectMax: number
  ) => {
    updateAddonsList(addonId, quantity, selectMax === 1, categoryId);
  };

  const addItemToCartMutation = useMutation({
    mutationFn: async (input: ProductSchema) => {
      // PRAT-187: a sacola só recebe quantidade que fecha a caixa.
      const formData = wholesaleFamily
        ? {
            ...input,
            quantity: packQuantity(wholesaleFamily, wholesaleUnitsInCart, Number(input.quantity)),
          }
        : input;
      console.log('🚀 addItemToCartMutation executando:', {
        editItemId,
        isEditMode,
        formData,
        productId,
      });

      const addons = formData.addons.map(addon => {
        // Encontra os dados completos do addon para obter addon_category_id
        const addonData = product?.addon_categories
          ?.flatMap(cat => cat.addons)
          .find(a => a.id === addon.id);

        // Vínculo correto adicional↔categoria: prioriza a categoria capturada na
        // seleção (addon.addon_category_id); só recorre à busca global (que casa
        // a primeira ocorrência) como fallback. Assim a regra/preço efetivos
        // levados ao carrinho são os da categoria certa.
        const categoryId = addon.addon_category_id || addonData?.addon_category_id || '';

        // Encontra a categoria para obter price_calculation_type
        const category = product?.addon_categories?.find(cat => cat.id === categoryId);

        return {
          id: addon.id,
          name: addon.name,
          price: addon.price,
          quantity: addon.quantity,
          addon_category_id: categoryId,
          consider_most_expensive: category?.consider_most_expensive,
          price_calculation_type: category?.price_calculation_type,
          // Teto individual do complemento levado ao carrinho para revalidar o
          // limite por complemento (issue 311) no seletor de make-order/cart.
          item_select_max: addonData?.select_max,
        };
      });

      // RN: Se tiver na url o editItemId, atualiza o item no carrinho
      if (editItemId) {
        console.log('✏️ EDITANDO item do carrinho:', {
          editItemId,
          quantity: formData.quantity,
          addons: addons.length,
        });
        return editItem.execute({
          id: editItemId,
          data: {
            quantity: Number(formData.quantity),
            observation: formData.observations,
            addons,
            // Preço de venda efetivo (promocional quando houver) levado ao checkout.
            price: effectivePrice,
            image_id: product?.image,
            category_id: product?.category_id ?? null,
            // Escolhas do combo configurável (PRAT-20); ausente em produto comum.
            combo_choices: isCombo ? comboChoices : undefined,
          },
        });
      }

      console.log('➕ ADICIONANDO novo item ao carrinho:', {
        item_id: formData.item_id,
        quantity: formData.quantity,
      });
      // Clube pratiGO (PRAT-27): produto aberto em modo resgate (`?clube=1`)
      // entra como troca por pontos, com o custo calculado sobre o preço final
      // (produto + complementos/escolhas), na mesma regra do backend.
      const clubRedeem = clubRedeemContext;
      const rewardPoints = clubRedeem
        ? pointsForPrice(Number(formData.total_price), clubRedeem.product_multiplier)
        : 0;

      if (clubRedeem && rewardPoints > clubRedeem.available_points) {
        throw new UIError(
          'CLUB_INSUFFICIENT_POINTS',
          `Você precisa de ${formatPoints(rewardPoints)} para trocar este produto e tem ${formatPoints(clubRedeem.available_points)}.`
        );
      }

      return addItem.execute({
        item_id: formData.item_id,
        quantity: Number(formData.quantity),
        store_slug: slug!,
        observation: formData.observations,
        addons,
        name: product?.name || '',
        // Preço de venda efetivo (promocional quando houver) levado ao checkout.
        price: effectivePrice,
        total_price: Number(formData.total_price),
        image_id: product?.image || null,
        // Categoria do produto (PRAT-184), para a promoção por quantidade.
        category_id: product?.category_id ?? null,
        // Escolhas do combo configurável (PRAT-20); ausente em produto comum.
        ...(isCombo ? { combo_choices: comboChoices } : {}),
        ...(clubRedeem ? { is_reward: true, loyalty_points_cost: rewardPoints } : {}),
      });
    },
    onError: async (error, variables) => {
      if (error instanceof UIError && error.code === 'ITEMS_WITH_DIFFERENT_STORE') {
        const shouldClearCart = window.confirm(
          `${error.message} Você deseja esvaziar o carrinho para adicionar um novo item?`
        );

        if (!shouldClearCart) return;

        await clearCart.execute();
        await queryClient.refetchQueries({ queryKey: ['cart-items'] });

        await addItemToCartMutation.mutateAsync(variables);
        setIsCartModalOpen(false);
        navigate('/cart/review-order');

        return;
      }
      if (error instanceof UIError && error.code === 'CLUB_INSUFFICIENT_POINTS') {
        enhancedToast.info('Ainda faltam pontos', { description: error.message });
        return;
      }
      enhancedToast.error('Erro ao adicionar item ao carrinho');
    },
  });

  // Handlers do carrinho
  const addToCart = (onSuccess?: () => void) => {
    const formData = watch();

    addItemToCartMutation.mutate(formData, {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: ['cart-items'] });

        track.addToCart(
          [
            {
              id: product!.id,
              quantity: Number(formData.quantity),
              // Preço de venda efetivo (promocional quando houver) reportado ao tracking.
              item_price: effectivePrice,
            },
          ],
          Number(formData.total_price)
        );

        // Chama callback de sucesso (para fechar o drawer com animação)
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 300);
        }
      },
    });
  };

  const handleIncrementAddon = (addonId: string, categoryId: string, maxForThisAddon: number) => {
    const currentQuantity = getAddonQuantity(addonId);
    if (currentQuantity < maxForThisAddon) {
      updateAddonsList(addonId, currentQuantity + 1, false, categoryId);
      return;
    }

    const category = product?.addon_categories?.find(cat => cat.id === categoryId);

    // Teto individual do complemento (issue 311): quando ESTE complemento atinge
    // o próprio limite, o click é bloqueado e avisamos com a mensagem específica
    // do complemento, mesmo que o grupo ainda tivesse espaço.
    const addon = category?.addons.find(item => item.id === addonId);
    if (addon?.select_max && currentQuantity >= addon.select_max) {
      enhancedToast.warning('Limite do complemento atingido', {
        description: getAddonLimitLabel(addon.select_max),
        id: `addon-item-limit-${addonId}`,
      });
      return;
    }

    // Avisar sobre o "máximo por complemento escolhido" (issue 311): o controle
    // de quantidade já bloqueia a seleção acima do teto; aqui explicamos o motivo
    // quando o cliente toca um complemento já no limite da categoria.
    if (category && category.select_max > 0) {
      enhancedToast.warning('Limite de complementos atingido', {
        description: getCategoryLimitMessage(category.select_max),
        id: `addon-limit-${categoryId}`,
      });
    }
  };

  const handleToggleRadioAddon = (addonId: string, categoryId: string) => {
    const isSelected = isAddonSelected(addonId);
    updateAddonsList(addonId, isSelected ? 0 : 1, true, categoryId);
    if (!isSelected) scheduleComboAdvance();
  };

  // Verifica se uma categoria obrigatória está completa
  // Considera tanto mandatory quanto select_min
  const isMandatoryCategoryComplete = (categoryId: string): boolean => {
    const category = product?.addon_categories?.find(cat => cat.id === categoryId);
    if (!category || category.mandatory !== 1) return false;

    const totalSelected = addons
      .filter(addon => {
        const addonData = product?.addon_categories
          ?.flatMap(cat => cat.addons)
          .find(a => a.id === addon.id);
        return addonData?.addon_category_id === categoryId;
      })
      .reduce((total, addon) => total + addon.quantity, 0);

    // Categoria está completa se:
    // 1. Tem select_min definido: precisa ter pelo menos select_min itens
    // 2. Não tem select_min: precisa ter pelo menos 1 item (mandatory)
    const minRequired = category.select_min > 0 ? category.select_min : 1;
    return totalSelected >= minRequired;
  };

  // Handlers de quantidade com atualização imutável a partir do estado atual.
  // Lê o valor corrente via getValues para evitar closures defasadas e calcula
  // o novo valor sem mutar o estado anterior.
  const handleIncrementQuantity = () => {
    const currentQuantity = getValues('quantity') || MIN_QUANTITY;
    setValue(
      'quantity',
      packQuantity(wholesaleFamily, wholesaleUnitsInCart, currentQuantity + wholesaleStep)
    );
  };

  const handleDecrementQuantity = () => {
    const currentQuantity = getValues('quantity') || MIN_QUANTITY;
    setValue(
      'quantity',
      packQuantity(
        wholesaleFamily,
        wholesaleUnitsInCart,
        Math.max(MIN_QUANTITY, currentQuantity - wholesaleStep)
      )
    );
  };

  const toggleCartModal = () => setIsCartModalOpen(prev => !prev);

  // Faz scroll até a categoria informada (com múltiplas tentativas) e dispara
  // a animação do badge para sinalizar visualmente o requisito não atendido.
  const scrollToCategory = (categoryId: string, attempts = 0) => {
    const maxAttempts = 5;
    const category = product?.addon_categories?.find(cat => cat.id === categoryId);

    // Tenta encontrar o elemento da categoria
    let categoryElement = document.querySelector(
      `[data-category-id="${categoryId}"]`
    ) as HTMLElement;

    // Se não encontrou, tenta buscar todos os elementos com data-category-id
    if (!categoryElement) {
      const allCategoryElements = document.querySelectorAll('[data-category-id]');
      categoryElement =
        (Array.from(allCategoryElements).find(
          el => el.getAttribute('data-category-id') === categoryId
        ) as HTMLElement) || null;
    }

    const scrollContainer = scrollContainerRef.current;

    console.log(`🔍 Tentativa ${attempts + 1}/${maxAttempts}:`, {
      categoryElement: !!categoryElement,
      scrollContainer: !!scrollContainer,
      categoryId,
    });

    if (categoryElement && scrollContainer) {
      // Usa requestAnimationFrame para garantir que o DOM está pronto
      requestAnimationFrame(() => {
        const elementRect = categoryElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const offset = 100; // Offset maior para garantir visibilidade

        const scrollPosition =
          scrollContainer.scrollTop + elementRect.top - containerRect.top - offset;

        console.log('🎯 Fazendo scroll para categoria:', {
          categoryName: category?.name,
          elementTop: elementRect.top,
          containerTop: containerRect.top,
          currentScroll: scrollContainer.scrollTop,
          targetScroll: scrollPosition,
        });

        // Scroll suave
        scrollContainer.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });

        // Ativa animação do badge
        console.log('💫 Ativando animação do badge');
        setAnimatingBadge(categoryId);
      });
    } else if (attempts < maxAttempts - 1) {
      // Se não encontrou, tenta novamente após um delay
      console.log('⏳ Elemento não encontrado, tentando novamente...');
      setTimeout(() => scrollToCategory(categoryId, attempts + 1), 100);
    } else {
      console.error('❌ Não foi possível encontrar o elemento após múltiplas tentativas');
    }
  };

  // Handlers do combo configurável (PRAT-20)
  const handleToggleComboOption = (group: ComboGroup, itemId: string) => {
    const selecting = !findComboSelection(comboSelections, group.id, itemId);
    setComboSelections(prev => toggleComboOption(group, itemId, prev));
    if (selecting && getMaxChoices(group) === 1) scheduleComboAdvance();
  };

  const handleComboOptionQuantityChange = (group: ComboGroup, itemId: string, value: number) => {
    setComboSelections(prev => setComboOptionQuantity(group, itemId, value, prev));
  };

  const handleComboChoiceAddonChange = (
    groupId: string,
    itemId: string,
    addonCategories: AddonCategory[],
    addonId: string,
    value: number,
    isRadio: boolean,
    categoryId: string
  ) => {
    setComboSelections(prev => {
      const current = prev.find(sel => sel.group_id === groupId && sel.item_id === itemId);
      if (!current) return prev;
      const nextAddons = applyAddonChange(
        current.addons,
        addonCategories,
        addonId,
        value,
        isRadio,
        categoryId
      );
      return setComboChoiceAddons(groupId, itemId, nextAddons, prev);
    });
    if (isRadio && value > 0) scheduleComboAdvance();
  };

  const handleAdvanceToDrawer = () => {
    console.log('🔘 Botão Adicionar/Salvar clicado');

    // Combo configurável (PRAT-20): cada grupo precisa estar dentro do mínimo e
    // do máximo de escolhas, e cada escolha personalizável dentro das regras dos
    // complementos do produto escolhido.
    // Na montagem em etapas, o que faltar leva de volta à etapa correspondente.
    if (isCombo && comboFirstBlocked) {
      enhancedToast.error(comboFirstBlocked.message);
      clearComboAdvance();
      setComboWizard({ index: comboFirstBlocked.index, reviewing: true });
      setAnimatingBadge(comboSteps[comboFirstBlocked.index]?.key ?? null);
      return;
    }
    if (isCombo && !comboValidation.isValid) {
      enhancedToast.error(comboValidation.message);
      if (comboValidation.groupId) {
        handleComboGoToStep(getComboGroupStepKey(comboValidation.groupId));
      }
      return;
    }

    // Usa a função checkMinimumRequirements que valida tanto mandatory quanto select_min
    const addonCategories = product?.addon_categories || [];
    const validation = checkMinimumRequirements(addons, addonCategories);

    console.log('📋 Validação de requisitos mínimos:', {
      isValid: validation.isValid,
      message: validation.message,
      categoryId: validation.categoryId,
    });

    if (!validation.isValid && validation.categoryId) {
      const category = addonCategories.find(cat => cat.id === validation.categoryId);
      console.log(
        `⚠️ Categoria "${category?.name}" não atende aos requisitos:`,
        validation.message
      );

      // Inicia o processo de scroll
      scrollToCategory(validation.categoryId);
      return;
    }

    // Valida o limite MÁXIMO de cada categoria pela soma total das quantidades
    // selecionadas. A UI já impede ultrapassar select_max durante a seleção,
    // mas esta checagem garante a regra também em fluxos que podem trazer um
    // estado fora do limite (ex.: edição de um item cuja configuração mudou).
    const maxValidation = checkMaximumRequirements(addons, addonCategories);

    console.log('📋 Validação de limite máximo:', {
      isValid: maxValidation.isValid,
      message: maxValidation.message,
      categoryId: maxValidation.categoryId,
    });

    if (!maxValidation.isValid && maxValidation.categoryId) {
      const category = addonCategories.find(cat => cat.id === maxValidation.categoryId);
      console.log(
        `⚠️ Categoria "${category?.name}" excedeu o limite máximo:`,
        maxValidation.message
      );

      enhancedToast.error(maxValidation.message);
      scrollToCategory(maxValidation.categoryId);
      return;
    }

    console.log('✅ Todos os requisitos atendidos, adicionando ao carrinho');
    // Se todas as validações passaram, adiciona ao carrinho e fecha o drawer
    addToCart(onCloseDrawer);
  };

  // Limpa o formulário quando muda de produto (novo produto)
  useEffect(() => {
    // Verifica se o productId mudou e não está em modo de edição
    const productIdChanged = lastProductIdRef.current !== productId;

    console.log('🔍 Verificando mudança de produto:', {
      productId,
      lastProductId: lastProductIdRef.current,
      productIdChanged,
      editItemId,
      isEditMode: !!editItemId,
    });

    if (productId && productIdChanged && !editItemId) {
      console.log('🆕 Novo produto detectado, limpando formulário:', {
        productId,
        lastProductId: lastProductIdRef.current,
      });

      form.reset({
        item_id: productId,
        addons: [],
        total_price: 0,
        quantity: 1,
        observations: '',
      });
      setComboSelections([]);
      setComboWizard({ index: 0, reviewing: false });

      console.log('✅ Formulário limpo para novo produto');
    }

    // Atualiza a ref com o productId atual
    lastProductIdRef.current = productId;
  }, [productId, editItemId, form]);

  // Carrega dados do item para edição
  useEffect(() => {
    if (cartItemQuery.data && editItemId) {
      console.log('🔄 Carregando dados do item para edição:', {
        editItemId,
        item: cartItemQuery.data,
        productId,
        productFromData: product?.id,
      });

      // Usa o productId atual (do produto encontrado) em vez do item_id null do carrinho
      const itemIdToUse = productId || product?.id || cartItemQuery.data.item_id;

      const resetData = {
        item_id: itemIdToUse,
        quantity: cartItemQuery.data.quantity,
        observations: cartItemQuery.data.observation,
        addons: cartItemQuery.data.addons,
        total_price: cartItemQuery.data.total_price,
      };

      console.log('📝 Dados para resetar o formulário:', resetData);
      form.reset(resetData);
      setComboSelections(fromCartComboChoices(cartItemQuery.data.combo_choices));
      // Edição reabre no resumo (a última etapa), com as escolhas preenchidas.
      setComboWizard({ index: Number.MAX_SAFE_INTEGER, reviewing: false });

      console.log('✅ Formulário resetado com dados do item');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItemQuery.data, editItemId, productId, product]);

  return {
    // Data
    product,
    totalPrice,
    quantityPromotionNote,
    wholesaleFamily,
    wholesaleUnitsInCart,
    wholesaleTotalPrice,
    handleSetQuantity,
    // Clube pratiGO (PRAT-27): modo resgate e custo em pontos do que está montado.
    isClubRedeem: clubRedeemContext !== null,
    clubRedeemPoints: clubRedeemContext
      ? pointsForPrice(totalPrice, clubRedeemContext.product_multiplier)
      : 0,
    filteredAddonCategories,
    storeInfo: merchantDetailsQuery.data,

    // Estado reativo da seleção de adicionais. Exposto como a própria lista
    // (derivada de `watch('addons')`) para que o conteúdo do drawer reaja às
    // mudanças e calcule quantidade/seleção/limite a partir da fonte reativa.
    selectedAddons: addons,

    // Combo configurável (PRAT-20)
    comboGroups,
    comboSelections,
    comboChoices,
    isComboValid: !isCombo || (comboValidation.isValid && !comboFirstBlocked),
    isComboWizard: isCombo,
    comboSteps,
    comboStep,
    comboStepIndex,
    comboStepBlocker,
    handleComboNext,
    handleComboBack,
    handleComboGoToStep,
    handleToggleComboOption,
    handleComboOptionQuantityChange,
    handleComboChoiceAddonChange,

    onCloseDrawer,

    // UI State
    isCartModalOpen,
    addonSearchTerm,
    animatingBadge,
    isDescriptionExpanded,
    isEditMode,

    // Handlers
    handleIncrementQuantity,
    handleDecrementQuantity,
    updateObservations: (newObservations: string) => setValue('observations', newObservations),

    handleAddToCart: addToCart,
    // Gravando o item no carrinho (spinner e trava do botão Adicionar/Salvar).
    isAdding: addItemToCartMutation.isPending,
    handleAddonSearchChange: (value: string) => setAddonSearchTerm(value),
    handleAdvanceToDrawer,
    handleIncrementAddon,
    handleAddonChange,
    handleToggleDescription: () => setIsDescriptionExpanded(prev => !prev),

    toggleCartModal,

    handleToggleRadioAddon,
    isMandatoryCategoryComplete,

    // Form
    watch,
    scrollContainerRef,
  };
}
